import { Check } from "lucide-react";
import { effectiveCell, cellValue, cellLabel, factionIcon } from "@/lib/game";
import ListDialog from "@/components/ListDialog";

/**
 * Live pairing matrix shown during the resolver.
 * - settled matchups: solid amber with a check
 * - predicted (optimal) future matchups: cyan ring "route"
 * - cells whose player is already paired: greyed out (no longer relevant)
 */
export default function ResolverMatrix({ setup, analysis }) {
  const yours = setup.players.filter((p) => p.team === setup.you_are);
  const opp = setup.players.filter((p) => p.team !== setup.you_are);
  if (!yours.length || !opp.length) return null;

  const you = setup.you_are;
  const orient = (p) => ({
    yourId: you === "A" ? p.a : p.b,
    oppId: you === "A" ? p.b : p.a,
  });

  const locked = analysis?.pairings || [];
  const route = analysis?.projection?.pairings || [];

  const lockedCells = new Set();
  const lockedYour = new Set();
  const lockedOpp = new Set();
  locked.forEach((p) => {
    const { yourId, oppId } = orient(p);
    lockedCells.add(`${yourId}|${oppId}`);
    lockedYour.add(yourId);
    lockedOpp.add(oppId);
  });
  const routeCells = new Set();
  route.forEach((p) => {
    const { yourId, oppId } = orient(p);
    routeCells.add(`${yourId}|${oppId}`);
  });

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[560px]">
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `130px repeat(${opp.length}, minmax(60px, 1fr))` }}>
          <div />
          {opp.map((o) => {
            const dim = lockedOpp.has(o.id);
            return (
              <ListDialog key={o.id} player={o}>
                <button className={`flex flex-col items-center pb-1 transition-opacity group ${dim ? "opacity-30" : ""}`} data-testid="resolver-opp-list-trigger">
                  <img src={factionIcon(o.faction)} alt="" className="h-5 w-5 object-contain mb-0.5 transition-transform group-hover:scale-110" onError={(e) => (e.currentTarget.style.display = "none")} />
                  <p className="text-[10px] font-semibold text-cyan-300 truncate max-w-full leading-tight text-center group-hover:underline">{o.name || "—"}</p>
                </button>
              </ListDialog>
            );
          })}

          {yours.map((y) => {
            const rowDim = lockedYour.has(y.id);
            return (
              <RmRow key={y.id} y={y} opp={opp} setup={setup} rowDim={rowDim}
                lockedCells={lockedCells} lockedYour={lockedYour} lockedOpp={lockedOpp} routeCells={routeCells} />
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-amber-500" /> Settled</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded border-2 border-cyan-400" /> Predicted route</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-slate-800 opacity-40" /> No longer available</span>
        </div>
      </div>
    </div>
  );
}

function RmRow({ y, opp, setup, rowDim, lockedCells, lockedYour, lockedOpp, routeCells }) {
  return (
    <>
      <ListDialog player={y}>
        <button className={`flex items-center gap-1.5 pr-2 transition-opacity group ${rowDim ? "opacity-30" : ""}`} data-testid="resolver-your-list-trigger">
          <img src={factionIcon(y.faction)} alt="" className="h-4 w-4 object-contain shrink-0 transition-transform group-hover:scale-110" onError={(e) => (e.currentTarget.style.display = "none")} />
          <p className="text-xs font-semibold text-amber-300 truncate group-hover:underline">{y.name || "—"}</p>
        </button>
      </ListDialog>
      {opp.map((o) => {
        const key = `${y.id}|${o.id}`;
        const cell = effectiveCell(setup, y.id, o.id, o.faction);
        const v = cellValue(cell);
        const isLocked = lockedCells.has(key);
        const isDead = !isLocked && (lockedYour.has(y.id) || lockedOpp.has(o.id));
        const isRoute = !isLocked && !isDead && routeCells.has(key);
        const base = cell?.tier
          ? { bg: v >= 0 ? "#064e3b" : "#5b1a1a", color: v >= 0 ? "#6ee7b7" : "#fca5a5" }
          : { bg: "#0f1623", color: "#475569" };
        return (
          <div
            key={o.id}
            data-testid="resolver-matrix-cell"
            className="relative min-h-[40px] rounded-md flex items-center justify-center font-mono text-xs font-bold transition-all"
            style={{
              background: base.bg,
              color: base.color,
              opacity: isDead ? 0.22 : 1,
              filter: isDead ? "grayscale(1)" : "none",
              outline: isLocked ? "2px solid #f59e0b" : isRoute ? "2px solid #22d3ee" : "1px solid #1e293b",
              outlineOffset: isLocked || isRoute ? "-1px" : "0",
              boxShadow: isLocked ? "0 0 12px rgba(245,158,11,0.4)" : isRoute ? "0 0 10px rgba(34,211,238,0.25)" : "none",
            }}
          >
            {isLocked && <Check className="h-3 w-3 absolute top-0.5 right-0.5 text-amber-300" />}
            {cell?.tier ? cellLabel(cell) : "—"}
          </div>
        );
      })}
    </>
  );
}
